import { memo } from "react";
import { useTranslation } from "react-i18next";
import {
    IconPlayerPlayFilled,
    IconPlayerPauseFilled,
    IconCheck,
    IconChecks,
    IconBulb,
    IconBulbOff,
    IconPictureInPicture,
    IconPictureInPictureOff,
    IconRectangle,
    IconRectangleFilled,
    IconMaximize,
    IconMinimize,
} from "@tabler/icons-react";

import ControlButton from "./ControlButton";
import PlaylistNavButton from "./PlaylistNavButton";
import VolumeControl from "./VolumeControl";
import TimeDisplay from "./TimeDisplay";
import SpeedMenu from "./SpeedMenu";
import CaptionsPanel from "./CaptionsPanel";
import AutoplaySwitch from "./AutoplaySwitch";

/**
 * ControlBar — the full set of custom controls, grouped in translucent pills.
 * Layout-agnostic: the horizontal bar and the vertical rail are pure CSS on
 * `.player-controls-row`.
 *
 * `disabled` renders every control inert (no video source yet): the bar keeps
 * its shape so the page layout does not shift when the video arrives.
 */
function ControlBar({
    videoRef,
    state,
    actions,
    captions,
    watched,
    onToggleWatched,
    prevContent,
    nextContent,
    onNavigate,
    autoplayEnabled,
    onAutoplayChange,
    single,
    vertical = false,
    disabled = false,
    embedded = false,
}) {
    const { t } = useTranslation("content");
    const {
        isPlaying, volume, muted, speed,
        isFullscreen, isPip, pipSupported, isTheater, isAmbient,
    } = state;

    return (
        <div className="player-controls-row" aria-disabled={disabled || undefined}>
            <div className="player-cluster player-cluster--main">
                <div className="player-pill">
                    <ControlButton
                        label={isPlaying ? t("player.pause") : t("player.play")}
                        onClick={actions.togglePlay}
                        disabled={disabled}
                    >
                        {isPlaying ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
                    </ControlButton>
                </div>

                {!single && (
                    <div className="player-pill">
                        <PlaylistNavButton direction="prev" content={prevContent} onNavigate={onNavigate} disabled={disabled} />
                        <PlaylistNavButton direction="next" content={nextContent} onNavigate={onNavigate} disabled={disabled} />
                    </div>
                )}

                <div className="player-pill">
                    <VolumeControl
                        volume={volume}
                        muted={muted}
                        onChangeVolume={actions.changeVolume}
                        onToggleMute={actions.toggleMute}
                        vertical={vertical}
                        disabled={disabled}
                    />
                </div>

                <TimeDisplay videoRef={videoRef} />
            </div>

            <div className="player-cluster player-cluster--settings">
                <div className="player-pill">
                    {!single && <AutoplaySwitch enabled={autoplayEnabled} onChange={onAutoplayChange} disabled={disabled} />}

                    <SpeedMenu speed={speed} onSetSpeed={actions.setSpeed} disabled={disabled} />

                    <CaptionsPanel
                        captions={captions.captions}
                        availableLangs={captions.availableLangs}
                        activeLang={captions.activeLang}
                        naturalLanguage={captions.naturalLanguage}
                        chromeHidden={state.isRest && isPlaying}
                        onSelect={captions.setCaptionLanguage}
                    />

                    <ControlButton
                        label={watched ? t("player.unmarkWatched") : t("player.markWatched")}
                        onClick={onToggleWatched}
                        active={watched}
                        className="player-invert-active"
                        disabled={disabled}
                    >
                        {watched ? <IconChecks /> : <IconCheck />}
                    </ControlButton>

                    <ControlButton
                        label={isAmbient ? t("player.ambientOff") : t("player.ambientOn")}
                        onClick={actions.toggleAmbient}
                        active={isAmbient}
                        className="player-invert-active"
                        disabled={disabled}
                    >
                        {isAmbient ? <IconBulb /> : <IconBulbOff />}
                    </ControlButton>

                    {pipSupported && (
                        <ControlButton label={t("player.pictureInPicture")} onClick={actions.togglePip} active={isPip} disabled={disabled}>
                            {isPip ? <IconPictureInPictureOff /> : <IconPictureInPicture />}
                        </ControlButton>
                    )}

                    {/* Theater mode is a Content-page layout: meaningless inside a modal. */}
                    {!embedded && (
                        <ControlButton
                            label={isTheater ? t("player.theaterExit") : t("player.theater")}
                            onClick={actions.toggleTheater}
                            active={isTheater}
                            className="player-theater-button"
                            disabled={disabled}
                        >
                            {isTheater ? <IconRectangleFilled /> : <IconRectangle />}
                        </ControlButton>
                    )}

                    <ControlButton
                        label={isFullscreen ? t("player.fullscreenExit") : t("player.fullscreen")}
                        onClick={actions.toggleFullscreen}
                        disabled={disabled}
                    >
                        {isFullscreen ? <IconMinimize /> : <IconMaximize />}
                    </ControlButton>
                </div>
            </div>
        </div>
    );
}

export default memo(ControlBar);
